import { useTranslation } from 'react-i18next';

import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { DocsOperation } from '../../generated/reference';

import { EndpointSection } from './endpoint-section';

interface Requirement {
  key: string;
  chip: string;
  hint: string;
  warn?: boolean;
}

/** What a call needs: the key, its scope, and the extra headers; plus the "real data" warning. */
export function EndpointRequirements({ operation }: { operation: DocsOperation }) {
  const { t } = useTranslation('docs');
  const requirements: Requirement[] = [
    {
      key: 'authorization',
      chip: t('endpoint.requirements.authorization'),
      hint: t('endpoint.requirements.authorizationHint'),
    },
    operation.scope
      ? {
          key: 'scope',
          chip: t('endpoint.requirements.scope', { scope: operation.scope }),
          hint: t('endpoint.requirements.scopeHint'),
        }
      : {
          key: 'scope',
          chip: t('endpoint.requirements.anyScope'),
          hint: t('endpoint.requirements.anyScopeHint'),
        },
  ];
  if (operation.customerToken) {
    requirements.push({
      key: 'customerToken',
      chip: t('endpoint.requirements.customerToken'),
      hint: t('endpoint.requirements.customerTokenHint'),
    });
  }
  if (operation.idempotent) {
    requirements.push({
      key: 'idempotencyKey',
      chip: t('endpoint.requirements.idempotencyKey'),
      hint: t('endpoint.requirements.idempotencyKeyHint'),
    });
  }
  if (operation.country) {
    requirements.push({
      key: 'country',
      chip: t('endpoint.requirements.country'),
      hint: t('endpoint.requirements.countryHint'),
    });
  }
  if (operation.mutates) {
    requirements.push({
      key: 'mutates',
      chip: t('endpoint.requirements.mutates'),
      hint: t('endpoint.requirements.mutatesHint'),
      warn: true,
    });
  }

  return (
    <EndpointSection id="requirements" title={t('endpoint.requirements.title')}>
      <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[auto_1fr]">
        {requirements.map((requirement) => (
          <div key={requirement.key} data-requirement={requirement.key} className="contents">
            <dt>
              <span
                className={cn(
                  'inline-flex h-6 items-center rounded-md border px-2 font-mono text-xs',
                  requirement.warn
                    ? 'bg-warning/15 text-warning-foreground border-warning/40 dark:text-warning'
                    : 'bg-muted/60',
                )}
              >
                {requirement.chip}
              </span>
            </dt>
            <dd className="text-muted-foreground mb-2 text-sm sm:mb-0 sm:self-center">
              {requirement.hint}
            </dd>
          </div>
        ))}
      </dl>
    </EndpointSection>
  );
}
